import { Tag, Image, Avatar } from "antd";
import { Link } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface ComplimentaryCouponRecord {
  membershipId?:      string;
  couponId?:          string;
  sessionId?:         string;
  memberId?:          string;
  memberName?:        string;
  memberPhone?:       string;
  memberPhoto?:       string;
  branchId?:          string;
  branch?:            string;
  planName?:          string;
  packageExpiry?:     string;
  serviceType?:       string;
  type?:              string;
  status?:            string;
  totalSessions?:     number;
  usedSessions?:      number;
  remainingSessions?: number;
  trainerId?:         string;
  trainerName?:       string;
  startedAt?:         string;
  endedAt?:           string;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const getStatusColor = (status: string | undefined): string => {
  switch (status) {
    case 'in_progress': return 'blue';
    case 'paused':      return 'orange';
    case 'completed':   return 'green';
    case 'pending':     return 'default';
    default:            return 'default';
  }
};

const getStatusText = (status: string | undefined): string => {
  switch (status) {
    case 'in_progress': return 'Live';
    case 'paused':      return 'Paused';
    case 'completed':   return 'Completed';
    case 'pending':     return 'Pending';
    default:            return status ?? '-';
  }
};

/* ─── Columns ────────────────────────────────────────────────────────── */

export const getComplimentaryCouponColumns = () => [
  {
    title:     'S.No',
    dataIndex: 'sNo',
    key:       'sno',
    width:     70,
    align:     'center' as const,
    fixed:     'left' as const,
  },
  {
    title:     'Branch',
    dataIndex: 'branch',
    key:       'branch',
    width:     180,
    render:    (text: string) => text || '-',
  },
  {
    title:     'Profile',
    dataIndex: 'memberPhoto',
    key:       'profile',
    width:     80,
    align:     'center' as const,
    render:    (photo: string, record: ComplimentaryCouponRecord) => {
      const img = photo
        ? <Image src={photo} alt="Profile" width={40} height={40} style={{ borderRadius: '50%', objectFit: 'cover' }} preview={false} />
        : <Avatar size={40} icon={<UserOutlined />} />;
      return record.memberId
        ? <Link to={`/user-detail/${record.memberId}/membership`} style={{ cursor: 'pointer' }}>{img}</Link>
        : img;
    },
  },
  {
    title:     'Name',
    dataIndex: 'memberName',
    key:       'name',
    width:     150,
    render:    (text: string, record: ComplimentaryCouponRecord) =>
      record.memberId ? (
        <Link to={`/user-detail/${record.memberId}/membership`} style={{ color: 'inherit', textDecoration: 'none' }}>
          {text || '-'}
        </Link>
      ) : (text || '-'),
  },
  {
    title:     'Phone Number',
    dataIndex: 'memberPhone',
    key:       'phoneNumber',
    width:     130,
    render:    (text: string) => text || '-',
  },
  {
    title:     'Service Type',
    dataIndex: 'serviceType',
    key:       'serviceType',
    width:     150,
    render:    (text: string) => {
      if (!text) return '-';
      return <span style={{ textTransform: 'capitalize' }}>{text.replace(/_/g, ' ')}</span>;
    },
  },
  {
    title:     'Type',
    dataIndex: 'type',
    key:       'type',
    width:     130,
    align:     'center' as const,
    render:    (type: string) => (
      <Tag color={type === 'complimentary' ? 'green' : 'blue'} style={{ textTransform: 'capitalize' }}>
        {type || 'N/A'}
      </Tag>
    ),
  },
  {
    title:     'Trainer',
    dataIndex: 'trainerName',
    key:       'trainer',
    width:     150,
    render:    (text: string) => text || '-',
  },
  {
    title:     'Status',
    dataIndex: 'status',
    key:       'status',
    width:     120,
    render:    (status: string) => (
      <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
    ),
  },
  {
    title:     'Session Date',
    dataIndex: 'startedAt',
    key:       'startDate',
    width:     160,
    render:    (text: string, record: ComplimentaryCouponRecord) => {
      if (!text) return '-';
      const d = new Date(text);
      if (isNaN(d.getTime())) return text;
      const date    = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      const time    = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      const endedAt = record.endedAt ? new Date(record.endedAt) : null;
      const endTime = endedAt && !isNaN(endedAt.getTime())
        ? endedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
        : null;
      return (
        <div>
          <div>{date}</div>
          <div style={{ fontSize: 12, color: 'var(--placeholder)' }}>
            {time}{endTime ? <span style={{ color: 'var(--muted)' }}> ({endTime})</span> : null}
          </div>
        </div>
      );
    },
  },
];
